import { useQuery } from '@apollo/client';
import { debounce as _debounce } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

import type { FilterOptionsResult, Query, QueryGetFilterOptionsArgs } from '../../../../../apis/graphql';
import {
  DEBOUNCE_DURATION_MS,
  DEFAULT_FILTER_LIMIT,
  DEFAULT_FILTER_OFFSET,
} from '../../../../../common/hooks/constants';
import { GET_FILTER_OPTIONS } from '../../../FilterPanel/queries/getFilterOptions';

type GetFilterOptionsData = Pick<Query, 'getFilterOptions'>;

// Concrete return type — all fields are always present, so callers can
// destructure without optional-chaining or non-null assertions. Still
// structurally assignable to UseOptions (superset of its required fields).
export type UseFilterOptions = {
  options: { label: string; value: string }[];
  loading: boolean;
  error: string | undefined;
  hasMore: boolean;
  searching: boolean;
  searchTerm: string;
  onSearch: (term: string) => void;
  onNext: () => Promise<void>;
  onResetOptions: () => void;
};

export const useGetFilterOptions = (filterId: string, isSearchable: boolean): UseFilterOptions => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searching, setSearching] = useState<boolean>(false);
  const [mergedResult, setMergedResult] = useState<FilterOptionsResult | null>(null);

  const { data, error, fetchMore, refetch } = useQuery<GetFilterOptionsData, QueryGetFilterOptionsArgs>(
    GET_FILTER_OPTIONS,
    {
      variables: {
        filterId,
        searchTerm: '',
        offset: DEFAULT_FILTER_OFFSET,
        limit: DEFAULT_FILTER_LIMIT,
      },
    }
  );

  const currentResult = mergedResult ?? data?.getFilterOptions;
  const hasMore = currentResult?.hasNextPage ?? false;

  const options = useMemo(() => (currentResult?.options ?? []).map((o) => ({ label: o.label, value: o.value })), [
    currentResult,
  ]);

  const debouncedSearch = useMemo(
    () =>
      _debounce(async (term: string): Promise<void> => {
        try {
          const { data: refetchedData } = await refetch({
            filterId,
            searchTerm: term,
            offset: DEFAULT_FILTER_OFFSET,
            limit: DEFAULT_FILTER_LIMIT,
          });
          setMergedResult(refetchedData?.getFilterOptions ?? null);
          setSearching(false);
        } catch (e) {
          console.warn('Filter search failed:', e);
          setSearching(false);
        }
      }, DEBOUNCE_DURATION_MS),
    [refetch, filterId]
  );

  const onSearch = useCallback(
    (term: string): void => {
      if (!isSearchable) return;
      setSearchTerm(term);
      setSearching(true);
      setMergedResult(null);
      debouncedSearch(term);
    },
    [debouncedSearch, isSearchable]
  );

  const onNext = useCallback(async (): Promise<void> => {
    // Data-driven pagination: a non-null nextCursor on the current page means the
    // backend fetcher is cursor-backed (it ignores offset), so round-trip the cursor
    // as `after`. A null/absent nextCursor means the offset-based path (KATANA-1185
    // fetchers), which stays backward compatible.
    const nextCursor = currentResult?.nextCursor ?? null;
    const variables =
      nextCursor !== null
        ? { filterId, searchTerm, after: nextCursor, limit: DEFAULT_FILTER_LIMIT }
        : {
            filterId,
            searchTerm,
            offset: currentResult?.options?.length ?? 0,
            limit: DEFAULT_FILTER_LIMIT,
          };

    try {
      const { data: nextData } = await fetchMore({ variables });

      const newOptions = nextData?.getFilterOptions?.options ?? [];
      const newHasNextPage = nextData?.getFilterOptions?.hasNextPage ?? false;

      setMergedResult((prev) => {
        const prevOptions = prev?.options ?? currentResult?.options ?? [];
        return {
          __typename: 'FilterOptionsResult' as const,
          options: [...prevOptions, ...newOptions],
          hasNextPage: newHasNextPage,
          totalCount: nextData?.getFilterOptions?.totalCount ?? null,
          nextCursor: nextData?.getFilterOptions?.nextCursor ?? null,
        };
      });
    } catch (e) {
      console.warn('Failed to load more filter options:', e);
    }
  }, [fetchMore, filterId, searchTerm, currentResult]);

  const onResetOptions = useCallback((): void => {
    setSearchTerm('');
    setSearching(false);
    setMergedResult(null);
  }, []);

  return {
    options,
    loading: !data && !error,
    error: error?.message,
    hasMore,
    searching,
    searchTerm,
    onNext,
    onResetOptions,
    onSearch,
  };
};
